import posthog from 'posthog-js';
import { Logger } from '_clean-code/utils/logger';
import { DurableStorageKeys, localStore } from '_clean-code/utils/storage';
import { getAuthChannel } from './authChannel';

export interface IdentifyUserParams {
  email: string;
  companyId?: number;
  name?: string;
  language?: string;
  role?: string;
  appVersion?: string;
  apiBaseUrl?: string;
}

/**
 * Person properties exactly as PostHog receives them. Built in one place so the
 * fingerprint below can never drift from the payload it is meant to describe.
 */
const buildPersonProperties = (params: IdentifyUserParams) => ({
  email: params.email,
  companyId: params.companyId,
  name: params.name,
  language: params.language,
  role: params.role,
  appVersion: params.appVersion,
  apiBaseUrl: params.apiBaseUrl,
  // email | okta | entra. Undefined for sessions that predate this.
  authChannel: getAuthChannel() ?? undefined
});

/**
 * `posthog.identify` already skips a repeat call carrying unchanged person
 * properties — but it compares against an in-memory field, so the comparison is
 * lost on every page load and the SDK re-sends a `$set` event each time. On
 * screens that reload on a timer (TV mode) that turned into thousands of
 * identical events per screen per day.
 *
 * Persisting the same comparison restores the intended behaviour across loads:
 * identify runs when the identity or any of its properties actually change, and
 * is a no-op otherwise. The fingerprint is stored next to PostHog's own
 * identity state and is cleared with it — see `clearAppStorage`.
 */
export const identifyUser = (params: IdentifyUserParams): boolean => {
  const personProperties = buildPersonProperties(params);
  const fingerprint = JSON.stringify({
    distinctId: params.email,
    personProperties
  });

  if (localStore.read(DurableStorageKeys.ANALYTICS_IDENTITY) === fingerprint) {
    return false;
  }

  try {
    posthog.identify(params.email, personProperties);
  } catch (error) {
    // Analytics must never break the app, and leaving the fingerprint unwritten
    // means the next render retries rather than silently skipping the user.
    Logger.error('identifyUser: posthog.identify failed', error);
    return false;
  }

  localStore.write(DurableStorageKeys.ANALYTICS_IDENTITY, fingerprint);
  Logger.info('identifyUser: identified user');
  return true;
};
